import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";

import { LegacyBridgeTransitionPage } from "../../../shared/custom-ui";
import {
  clearHttpAccessToken,
  readHttpAccessToken,
  writeHttpAccessToken,
} from "../../../lib/http/accessToken";
import { loginWithLegacySingleLogin } from "../service";
import { resetAuthSessionQueries } from "./resetAuthSessionQueries";

const LEGACY_SINGLE_LOGIN_PARAM = "SL_No";
const LEGACY_SINGLE_LOGIN_STORAGE_PREFIX = "flowermaster.legacy_single_login.";
const TOKEN_EXPIRATION_SKEW_MS = 30_000;

const legacySingleLoginRequests = new Map<string, Promise<void>>();

type LegacySingleLoginRecord = {
  accessToken: string;
  expiresAt: number;
  singleLoginNo: string;
  userId: string;
};

type Props = {
  children: ReactNode;
};

/** 处理旧系统携带 SL_No 跳转到新系统时的强制登录与 URL 清理。 */
export function LegacySingleLoginGate({ children }: Props) {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const singleLoginNo = useMemo(() => readSingleLoginNo(location.search), [location.search]);
  const cleanedLocation = useMemo(
    () => removeSingleLoginNoFromLocation(location.pathname, location.search, location.hash),
    [location.hash, location.pathname, location.search],
  );
  const [status, setStatus] = useState<"ready" | "processing" | "failed">(
    singleLoginNo ? "processing" : "ready",
  );

  useEffect(() => {
    if (!singleLoginNo) {
      setStatus("ready");
      return;
    }

    let active = true;
    setStatus("processing");
    void runLegacySingleLogin(singleLoginNo, queryClient)
      .then(() => {
        if (!active) {
          return;
        }
        setStatus("ready");
        navigate(cleanedLocation, { replace: true });
      })
      .catch(() => {
        if (!active) {
          return;
        }
        clearHttpAccessToken();
        removeLegacySingleLoginRecord(singleLoginNo);
        resetAuthSessionQueries(queryClient);
        setStatus("failed");
      });

    return () => {
      active = false;
    };
  }, [cleanedLocation, navigate, queryClient, singleLoginNo]);

  if (!singleLoginNo) {
    return <>{children}</>;
  }

  if (status === "failed") {
    return (
      <LegacyBridgeTransitionPage
        ariaLabel="旧系统单点登录状态"
        className="auth-legacy-single-login-state"
        eyebrow="旧系统跳转"
        title="旧系统单点登录失败"
        description="请返回旧系统重新进入。"
        role="alert"
        status="error"
      />
    );
  }

  return (
    <LegacyBridgeTransitionPage
      ariaLabel="旧系统单点登录状态"
      className="auth-legacy-single-login-state"
      eyebrow="旧系统跳转"
      title="正在登录..."
      description="正在切换到新系统账号，请稍候。"
      role="status"
      status="loading"
    />
  );
}

/** 执行旧系统单点登录，并合并同一单点号的并发请求。 */
async function runLegacySingleLogin(singleLoginNo: string, queryClient: QueryClient): Promise<void> {
  if (shouldReuseCurrentLegacyLogin(singleLoginNo)) {
    return;
  }

  const pending = legacySingleLoginRequests.get(singleLoginNo);
  if (pending) {
    return pending;
  }

  const request = loginWithLegacySingleLogin(singleLoginNo)
    .then((session) => {
      writeHttpAccessToken(session.accessToken);
      resetAuthSessionQueries(queryClient);
      writeLegacySingleLoginRecord(singleLoginNo, {
        accessToken: session.accessToken,
        expiresAt: Date.now() + session.expiresIn * 1000,
        singleLoginNo,
        userId: String(session.user.id),
      });
    })
    .finally(() => {
      legacySingleLoginRequests.delete(singleLoginNo);
    });
  legacySingleLoginRequests.set(singleLoginNo, request);
  return request;
}

/** 判断当前 token 是否已经是该旧系统单点号换取的有效登录态。 */
function shouldReuseCurrentLegacyLogin(singleLoginNo: string): boolean {
  const currentToken = readHttpAccessToken();
  if (!currentToken) {
    return false;
  }
  const record = readLegacySingleLoginRecord(singleLoginNo);
  if (!record || record.accessToken !== currentToken) {
    return false;
  }
  if (record.expiresAt <= Date.now() + TOKEN_EXPIRATION_SKEW_MS) {
    return false;
  }
  return readTokenUserId(currentToken) === record.userId;
}

/** 从查询参数中读取旧系统单点号。 */
function readSingleLoginNo(search: string): string | null {
  const value = new URLSearchParams(search).get(LEGACY_SINGLE_LOGIN_PARAM)?.trim();
  return value || null;
}

/** 清理 URL 中的单点号，保留其它业务查询参数和 hash。 */
function removeSingleLoginNoFromLocation(
  pathname: string,
  search: string,
  hash: string,
): string {
  const params = new URLSearchParams(search);
  params.delete(LEGACY_SINGLE_LOGIN_PARAM);
  const nextSearch = params.toString();
  return `${pathname}${nextSearch ? `?${nextSearch}` : ""}${hash}`;
}

/** 从 JWT payload 中读取用户 ID，前端只用于重复登录抑制。 */
function readTokenUserId(token: string): string | null {
  const payload = token.split(".")[1];
  if (!payload) {
    return null;
  }
  try {
    const normalizedPayload = payload.replace(/-/g, "+").replace(/_/g, "/");
    const paddedPayload = normalizedPayload.padEnd(
      normalizedPayload.length + ((4 - (normalizedPayload.length % 4)) % 4),
      "=",
    );
    const decoded = JSON.parse(window.atob(paddedPayload)) as Record<string, unknown>;
    const rawUserId = decoded.user_id ?? decoded.sub;
    return rawUserId == null ? null : String(rawUserId);
  } catch {
    return null;
  }
}

/** 读取当前会话中已完成的旧系统单点登录记录。 */
function readLegacySingleLoginRecord(singleLoginNo: string): LegacySingleLoginRecord | null {
  try {
    const raw = window.sessionStorage.getItem(legacySingleLoginStorageKey(singleLoginNo));
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as Partial<LegacySingleLoginRecord>;
    if (!parsed.accessToken || !parsed.singleLoginNo || !parsed.userId || !parsed.expiresAt) {
      return null;
    }
    return {
      accessToken: parsed.accessToken,
      expiresAt: Number(parsed.expiresAt),
      singleLoginNo: parsed.singleLoginNo,
      userId: parsed.userId,
    };
  } catch {
    return null;
  }
}

/** 记录旧系统单点号与本次换取的 token 关系。 */
function writeLegacySingleLoginRecord(
  singleLoginNo: string,
  record: LegacySingleLoginRecord,
): void {
  try {
    window.sessionStorage.setItem(
      legacySingleLoginStorageKey(singleLoginNo),
      JSON.stringify(record),
    );
  } catch {
    // sessionStorage 不可用时仍可完成本次登录，只是无法抑制下一次同 token 重复登录。
  }
}

/** 删除失败或失效的旧系统单点登录记录。 */
function removeLegacySingleLoginRecord(singleLoginNo: string): void {
  try {
    window.sessionStorage.removeItem(legacySingleLoginStorageKey(singleLoginNo));
  } catch {
    // 忽略浏览器存储不可用。
  }
}

/** 构建按单点号隔离的会话存储 key。 */
function legacySingleLoginStorageKey(singleLoginNo: string): string {
  return `${LEGACY_SINGLE_LOGIN_STORAGE_PREFIX}${encodeURIComponent(singleLoginNo)}`;
}
